import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import LoadIcon from '../../LoadIcon';

// The proof pane from screen 3 of the approved design: the inbox line above,
// then the render itself at true 600px on #EFEAE7 — the emails' own paper
// colour — inside crop marks.
//
// What sits in that frame is the same sandboxed iframe the screens used before
// this restyle, not a picture of one. `sandbox=""` (empty, not omitted) is the
// point of it: the value applies every restriction at once, so a template's own
// scripts, forms, popups and top-level navigation are all inert while it is
// being previewed inside the CMS. The design's chrome is wrapped around it; the
// isolation is unchanged.
//
// The three device buttons switch what the frame holds:
//   600px      the render at the width every one of these templates is built for
//   Mobile     the same render at 375px, where the 600px tables start to fight
//   Plain text the text/plain alternative — the part that is otherwise invisible
//              until someone with images off receives it
//
// A FOURTH MODE, and only when a caller passes `children`. In the block builder
// this same pane is the editing surface: the canvas goes in as `children` under
// an "Edit" button that comes first and is where the pane opens. That is the
// whole layout decision — the email is edited in the place the email is shown,
// not in a column beside it.
//
// The iframe is untouched by that and stays reachable at all times, because it
// is the only thing on this screen that shows what the SERVER will send: the
// canvas is React elements drawing the same numbers, and the honest check
// against it drifting is to look at the real render. Everything about the
// sandbox, the srcDoc and the three device widths below is exactly as it was.
const BASE_VIEWS = [
  { key: 'desktop', label: '600px' },
  { key: 'mobile', label: 'Mobile' },
  { key: 'text', label: 'Plain text' },
];

/*
The frame, built by hand rather than written as JSX.

An <iframe srcDoc={html} /> in the render tree stopped painting: the element sits
there at its full size, `load` fires, the srcdoc attribute holds the whole
document, and the frame stays empty so the pane's own background shows through.
It was intermittent, which is what made it expensive to pin down.

What finally separated the cases was building the same frame two ways on the same
page: an element created with document.createElement, given the SAME srcdoc and
the same `sandbox=""`, and appended to the SAME container, renders every time —
while the React-rendered one beside it does not. Ruled out along the way, each
checked against the live page rather than assumed: the HTML itself (it renders
fine elsewhere), the sandbox (a sandboxed hand-built frame renders), the CSS
(no transform/filter/contain/content-visibility on any ancestor; height in px
behaves the same as 70vh), a React `key` to force a remount, withholding the
frame until the html arrives, a blob: URL (which a sandboxed frame refuses
outright), and SSR hydration (this page server-renders no iframe at all).

So the frame is created imperatively, which is the construction that was measured
to work. Everything else is unchanged — same `sandbox=""`, same class, same
srcdoc. Rebuilt from scratch whenever the document changes, because re-pointing a
live frame's srcdoc is one of the things that does not reliably re-navigate.
*/
const ProofFrame = ({ html, title }) => {
  const host = useRef(null);

  useEffect(() => {
    const node = host.current;
    if (!node) return undefined;

    while (node.firstChild) node.removeChild(node.firstChild);
    if (!html) return undefined;

    const frame = document.createElement('iframe');
    frame.setAttribute('sandbox', '');
    frame.className = 'proof-iframe';
    frame.title = title;
    frame.srcdoc = html;
    node.appendChild(frame);

    return () => {
      while (node.firstChild) node.removeChild(node.firstChild);
    };
  }, [html, title]);

  return <span ref={host} className="proof-framehost" />;
};

ProofFrame.propTypes = {
  html: PropTypes.string,
  title: PropTypes.string,
};

ProofFrame.defaultProps = { html: '', title: 'Proof' };

const EDIT_VIEW = { key: 'edit', label: 'Edit' };

const EmailProofPane = ({
  title, note, fromName, subject, preheader, html, text, isLoading, error, children, overlay,
  compareHtml, compareNote,
}) => {
  const editable = Boolean(children);
  const [view, setView] = useState(editable ? EDIT_VIEW.key : 'desktop');

  // Both directions of the builder appearing and disappearing mid-session.
  // "Start from blocks" hands this pane a canvas it did not have, and the user
  // asked to author an email, so it opens on it. "Edit the HTML instead" takes
  // the canvas away underneath a pane that is showing it, so it falls back to
  // the render rather than to an empty frame.
  //
  // Only on the TRANSITION, which is what the ref is for: switching to 600px
  // and back is a deliberate choice and must not be overruled on the next
  // render.
  const wasEditable = useRef(editable);
  useEffect(() => {
    if (editable && !wasEditable.current) setView(EDIT_VIEW.key);
    if (!editable && view === EDIT_VIEW.key) setView('desktop');
    wasEditable.current = editable;
  }, [editable, view]);

  const views = editable ? [EDIT_VIEW].concat(BASE_VIEWS) : BASE_VIEWS;
  const isEditing = editable && view === EDIT_VIEW.key;

  return (
    <div className="panel">
      <div className="head">
        <h4>{title}</h4>
        {note && <span className="locked marked">{note}</span>}
        <div className="right">
          <div className="devices">
            {views.map(({ key, label }) => (
              <button
                key={key}
                type="button"
                className={view === key ? 'on' : undefined}
                onClick={() => setView(key)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className={isEditing ? 'proofwrap is-editing' : 'proofwrap'}>
        <div className="inbox">
          <span className="from">{fromName}</span>
          <span className="subj">{subject}</span>
          <span className="pre">{preheader || 'No preheader — clients scrape the first line of the body instead.'}</span>
          <span className="tag">{isEditing ? 'Inbox list preview · edited above' : 'Inbox list preview'}</span>
        </div>

        {/* The email, with the selected block's properties floating over it.
            Floating rather than docked: a column took a third of the email's
            width, and the email is the thing being worked on. The panel is
            pinned bottom-right (see emails.css) so it sits over the pane's own
            background rather than over the part of the card people compose
            into. */}
        {/* A FIFTH MODE, for one moment only: the conversion review.

            `compareHtml` is the email AS IT IS SAVED TODAY, and when it is
            given, the editing surface is put beside it rather than in place of
            it. That is the whole safety story of converting a live template:
            nobody can honestly accept a conversion they have only seen on its
            own, because the question is never "is this a reasonable email", it
            is "is this THAT email". Two frames, same width, same paper colour,
            side by side, and the decision is obvious in a second.

            The left frame is a `sandbox=""` iframe exactly like the proof's own
            — this is stored template HTML and gets the same isolation. The right
            is the live canvas, still editable while it is being compared. */}
        {isEditing && compareHtml ? (
          <div className="comparesplit">
            <div className="comparepane">
              <div className="comparehead">
                <b>Now</b>
                <span>{compareNote}</span>
              </div>
              <div className="paperproof">
                <ProofFrame html={compareHtml} title={`${title} — before converting`} />
              </div>
            </div>
            <div className="comparepane">
              <div className="comparehead">
                <b>After converting</b>
                <span>Editable. Nothing is written until you save.</span>
              </div>
              <div className="buildersplit">
                {children}
                {overlay}
              </div>
            </div>
          </div>
        ) : null}

        {isEditing && !compareHtml ? (
          <div className="buildersplit">
            {children}
            {overlay}
          </div>
        ) : null}

        {!isEditing && isLoading && (
          <div className="emails-loading">
            <LoadIcon />
            Rendering proof…
          </div>
        )}

        {!isEditing && !isLoading && error && (
          <div className="banner bad">
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="#e3342f" strokeWidth="1.7">
              <path d="M8 2l6.4 11.2H1.6L8 2z" strokeLinejoin="round" />
              <path d="M8 6.6v2.6M8 11h.01" strokeLinecap="round" />
            </svg>
            <span>
              <b>The proof failed to render.</b>
              {` ${error}`}
            </span>
          </div>
        )}

        {!isEditing && !isLoading && !error && view === 'text' && (
          <div className="paperproof plain">
            <pre className="proof-text">
              {text || 'This template has no text/plain alternative.'}
            </pre>
          </div>
        )}

        {!isEditing && !isLoading && !error && view !== 'text' && (
          <div className={view === 'mobile' ? 'paperproof mobile' : 'paperproof'}>
            <ProofFrame html={html} title={title} />
          </div>
        )}
      </div>
    </div>
  );
};

EmailProofPane.propTypes = {
  title: PropTypes.string,
  note: PropTypes.string,
  fromName: PropTypes.string,
  subject: PropTypes.string,
  preheader: PropTypes.string,
  html: PropTypes.string,
  text: PropTypes.string,
  isLoading: PropTypes.bool,
  error: PropTypes.string,
  // The editable surface. Its presence is what adds the "Edit" mode; without it
  // this pane is exactly the three-device proof it has always been.
  children: PropTypes.node,
  // Shown over the editable surface only. The block properties panel.
  overlay: PropTypes.node,
  // The email as it is stored today. Given only during a conversion review, and
  // its presence is what splits the editing mode into two frames.
  compareHtml: PropTypes.string,
  compareNote: PropTypes.string,
};

EmailProofPane.defaultProps = {
  title: 'Proof',
  note: null,
  fromName: 'Sender',
  subject: '',
  preheader: '',
  html: '',
  text: '',
  isLoading: false,
  error: null,
  children: null,
  overlay: null,
  compareHtml: '',
  compareNote: 'The saved email. Untouched.',
};

export default EmailProofPane;
